import type { SimulationDetailView } from '../../api/types';
import { formatCurrency, formatPercent } from '../../lib/format';

interface LevelDetail {
  key: { saleType: string; territory: string; partyId: string };
  level: number;
  originalNetReceipts: number;
  priorDeductions: number;
  remainingReceipts: number;
  sharePercent: number;
  setLevelValue: number;
}

function isLevelDetail(value: unknown): value is LevelDetail {
  if (value === null || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  if (row.key === null || typeof row.key !== 'object') return false;
  const key = row.key as Record<string, unknown>;
  return (
    ['saleType', 'territory', 'partyId'].every((field) => typeof key[field] === 'string') &&
    [
      'level',
      'originalNetReceipts',
      'priorDeductions',
      'remainingReceipts',
      'sharePercent',
      'setLevelValue',
    ].every((field) => typeof row[field] === 'number' && Number.isFinite(row[field]))
  );
}

/** Displays persisted server calculations; the browser never recalculates payments. */
export function LevelDeductionsTable({
  simulation,
  partyId = '',
}: {
  simulation: SimulationDetailView;
  partyId?: string;
}) {
  const rows = simulation.results
    .filter(
      (line) =>
        (!partyId || line.partyId === partyId) &&
        (line.lineType === 'share_percentage' || line.lineType === 'share_due'),
    )
    .flatMap((line) =>
      Array.isArray(line.detail.levelDeductions)
        ? line.detail.levelDeductions.filter(isLevelDetail)
        : [],
    )
    .sort(
      (a, b) =>
        a.key.partyId.localeCompare(b.key.partyId) ||
        a.key.saleType.localeCompare(b.key.saleType) ||
        a.key.territory.localeCompare(b.key.territory) ||
        a.level - b.level,
    );
  if (!rows.length) return null;
  const partyName = (id: string) =>
    simulation.resultsSummary?.perParty.find((party) => party.partyId === id)?.partyName ?? id;
  return (
    <div className="rfp-stage-scroll">
      <p>
        Each sale type, territory and party has its own level deductions. Entitlements are
        calculated before deficit and advance recoupment.
      </p>
      <table className="rfp-stage-table">
        <caption>Level deductions by sale type, territory and party</caption>
        <thead>
          <tr>
            <th>Party</th>
            <th>Sale type</th>
            <th>Territory</th>
            <th>Level</th>
            <th>Original net receipts</th>
            <th>Prior deductions</th>
            <th>Remaining receipts</th>
            <th>Share</th>
            <th>Entitlement before recoupment</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={JSON.stringify([
                row.key.saleType,
                row.key.territory,
                row.key.partyId,
                row.level,
              ])}
            >
              <th scope="row">{partyName(row.key.partyId)}</th>
              <td>{row.key.saleType || 'Unspecified'}</td>
              <td>{row.key.territory || 'Unspecified'}</td>
              <td>{row.level}</td>
              <td>{formatCurrency(row.originalNetReceipts, simulation.currency)}</td>
              <td>{formatCurrency(row.priorDeductions, simulation.currency)}</td>
              <td>{formatCurrency(row.remainingReceipts, simulation.currency)}</td>
              <td>{formatPercent(row.sharePercent)}</td>
              <td>{formatCurrency(row.setLevelValue, simulation.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
